/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, type ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';

export default function ContactUs(): ReactElement {
    const [topic, setTopic] = useState<string>('');
    const [isPhoneExpanded, setIsPhoneExpanded] = useState(false);

    return (
        <div className="pb-16">
            <SeoMeta
                title="Contact Us — Baggallini"
                description="Get in touch with Baggallini customer support by chat or phone. We're available Monday–Friday, 9am–5pm ET at 1-800-448-8753."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Contact Us</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="max-w-screen-lg mx-auto px-6 pt-10">
                <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light tracking-wide mb-4">
                    Contact Us
                </h1>
                <p className="text-muted-foreground mb-10 max-w-xl">
                    Our customer service team is here to help. Select a topic below and we&apos;ll get you to the right
                    place.
                </p>

                {/* Topic selector */}
                <div className="mb-10 max-w-xs">
                    <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground mb-2">
                        How can we help?
                    </label>
                    <select
                        id="contact-topic"
                        value={topic}
                        onChange={(e) => {
                            setTopic(e.target.value);
                            setIsPhoneExpanded(false);
                        }}
                        className="w-full border border-[#E0DDD8] rounded-none px-4 py-3 text-sm bg-background focus:outline-none focus:border-foreground transition-colors">
                        <option value="">Select a topic...</option>
                        <option value="order">Order Status</option>
                        <option value="shipping">Shipping Issue</option>
                        <option value="return">Return or Refund</option>
                        <option value="exchange">Exchange</option>
                        <option value="product">Product Inquiry</option>
                        <option value="defective">Product Issue / Defective</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                {/* Contact card — shown once a topic is selected */}
                {topic && (
                    <div className="bg-[#F5F3F0] rounded-2xl px-8 py-8 max-w-lg mb-10">
                        <h2
                            style={{ fontFamily: 'var(--font-display)' }}
                            className="text-3xl font-light mb-3 text-foreground">
                            Reach Our Team
                        </h2>
                        <p className="text-muted-foreground text-sm mb-7">
                            Our customer service team can help with{' '}
                            {topic === 'order'
                                ? 'order status questions'
                                : topic === 'shipping'
                                  ? 'shipping issues'
                                  : topic === 'return'
                                    ? 'returns and refunds'
                                    : topic === 'exchange'
                                      ? 'exchanges'
                                      : topic === 'product'
                                        ? 'product questions'
                                        : topic === 'defective'
                                          ? 'defective products'
                                          : 'your inquiry'}
                            .
                        </p>

                        <p className="text-xl font-semibold leading-snug text-foreground mb-7">
                            For the fastest response, start a chat using the icon in the bottom-right corner.
                        </p>

                        <div className="space-y-6">
                            <div>
                                <p className="text-xs font-medium tracking-[0.1em] uppercase text-foreground mb-1">
                                    Hours
                                </p>
                                <p className="text-sm text-muted-foreground">Monday – Friday: 9 AM – 5 PM ET</p>
                            </div>
                        </div>

                        <div className="mt-7">
                            <button
                                type="button"
                                aria-expanded={isPhoneExpanded}
                                aria-controls="contact-phone-details"
                                onClick={() => setIsPhoneExpanded((expanded) => !expanded)}
                                className="flex w-full cursor-pointer items-center justify-between gap-3 text-left text-sm text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground">
                                <span>
                                    If you prefer to call,{' '}
                                    <span className="underline underline-offset-2">click here</span> for contact
                                    information
                                </span>
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    className={`size-4 shrink-0 transition-transform duration-200 ${isPhoneExpanded ? 'rotate-180' : ''}`}>
                                    <path
                                        d="m5 12.5 5-5 5 5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>

                            {isPhoneExpanded && (
                                <div id="contact-phone-details" className="pt-5">
                                    <p className="text-xs font-medium tracking-[0.1em] uppercase text-foreground mb-1">
                                        Phone
                                    </p>
                                    <a
                                        href="tel:18004488753"
                                        className="text-xl font-medium text-foreground hover:underline">
                                        1-800-448-8753
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Helpful links */}
                <div className="border-t border-[#E8E8E8] pt-8 mt-4">
                    <p className="text-sm font-medium text-foreground mb-4">You may also find these helpful:</p>
                    <div className="flex flex-wrap gap-3">
                        {[
                            { label: 'FAQ', href: '/faq' },
                            { label: 'Returns & Exchanges', href: '/returns' },
                            { label: 'Bag Size Guide', href: '/size-guide' },
                        ].map((l) => (
                            <Link
                                key={l.label}
                                to={l.href}
                                className="text-sm border border-foreground/30 rounded-full px-4 py-1.5 transition-colors hover:bg-foreground hover:text-background">
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
